<script lang="ts" setup>
  import type { RefComponent } from '@/types'

  import { ref, computed } from 'vue'
  import { useBreakpoints, useElementSize } from '@vueuse/core'

  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui'

  import bannerJpg from '@/assets/customer/images/banner-glasses/glasses-promo.jpg'
  import bannerJpg2x from '@/assets/customer/images/banner-glasses/glasses-promo@2x.jpg'
  import bannerWebp from '@/assets/customer/images/banner-glasses/glasses-promo.webp'
  import bannerWebp2x from '@/assets/customer/images/banner-glasses/glasses-promo@2x.webp'

  const props = withDefaults(
    defineProps<{
      modalWrapperClasses?: string
      modalClasses?: string
      headerClasses?: string
      bodyClasses?: string
      closeableByBackdropClick?: boolean
      position?: 'center' | 'top'
      mobileHeight?: 'fullscreen' | 'auto'

      imageUrl?: string
      imageAlt?: string
      imageClasses?: string
    }>(),
    {
      headerClasses: 'h-[64px] lg:h-[112px] py-4 border-mbrd border-b px-6',
      bodyClasses: 'px-6',
      modalWrapperClasses: 'md:max-w-[360px] lg:max-w-[440px]',
      modalClasses: 'pb-10',

      closeableByBackdropClick: true,

      position: 'center',
      mobileHeight: 'fullscreen',

      imageUrl: undefined,
      imageAlt: '',
      imageClasses: 'w-[46%] lg:w-1/2',
    }
  )

  const emit = defineEmits<{
    (e: 'close'): void
  }>()

  const wrapperRef: RefComponent<typeof HTMLDivElement> = ref(null)

  const breakpoints = useBreakpoints({
    md: 768,
    lg: 1024,
  })
  const isTabletOrDesktop = breakpoints.greaterOrEqual('md')
  const isDesktop = breakpoints.greaterOrEqual('lg')

  const { height } = useElementSize(wrapperRef)

  const ui = useUiStore()
  const {
    overflowHidden,
    ticker1Visibility,
    ticker2Visibility,
    ticker2VisibilityByPage,
  } = storeToRefs(ui)

  const style = computed(() => {
    let marginTop = null

    // if (!isTabletOrDesktop.value && props.position === 'center') return ''
    if (props.position === 'top') {
      // модалка поиска в самом верху; учитываем ticker 1 и 2 (баннер-полосы)
      const factor =
        (ticker1Visibility.value ? 1 : 0) +
        (ticker2Visibility.value && ticker2VisibilityByPage.value ? 1 : 0)
      const bannerLineHeight = 24 * factor

      const headerHeight = isDesktop.value ? 52 : 48

      marginTop = `${bannerLineHeight + headerHeight}px`
    } else {
      marginTop = `calc(50vh - ${Math.floor(height.value / 2)}px);`
    }

    return `margin-top: ${marginTop};`
  })

  const active = ref(false)

  function show() {
    active.value = true
    overflowHidden.value = true
  }
  function close() {
    active.value = false
    overflowHidden.value = false

    emit('close')
  }
  function handleBackdropClick() {
    if (props.closeableByBackdropClick) close()
  }

  defineExpose({ active, show, close })
</script>

<template>
  <transition name="fade">
    <div
      v-if="active"
      class="fixed left-0 top-0 z-[1055] bg-black/20 h-full w-full overflow-y-auto overflow-x-hidden outline-none"
      :class="[
        {
          flex:
            !isTabletOrDesktop &&
            props.mobileHeight === 'auto' &&
            props.position !== 'top',
        },
      ]"
      tabindex="-1"
      aria-modal="true"
      role="dialog"
      @pointerdown.self="handleBackdropClick"
      @keyup.esc="close"
    >
      <div
        ref="wrapperRef"
        class="w-auto mx-auto relative flex pointer-events-none bg-white"
        :class="[props.modalWrapperClasses]"
        :style="style"
      >
        <div class="shrink-0" :class="props.imageClasses">
          <picture>
            <source
              :srcset="`${bannerWebp} 1x, ${bannerWebp2x} 2x`"
              type="image/webp"
            />
            <source
              :srcset="`${bannerJpg} 1x, ${bannerJpg2x} 2x`"
              type="image/jpeg"
            />
            <img
              :src="bannerJpg"
              alt="Промо баннер очков"
              class="w-full h-full object-cover object-center"
            />
          </picture>
        </div>

        <div
          class="bg-white md:relative pointer-events-auto"
          :class="props.modalClasses"
        >
          <!-- Close Button -->
          <button
            class="absolute top-[15px] right-[15px] w-[45px] h-[45px] bg-white bg-white/70 flex justify-center items-center rounded-full md:top-[26px] md:right-[23px] md:w-[28px] md:h-[28px] close cursor-pointer bg-[rgba(255, 255, 255, 0.7)] transition-transform duration-300 md:hover:scale-105 active:scale-95 active:bg-white"
            aria-label="close"
            @click.prevent="close"
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="w-[24px] h-[24px] md:w-full md:h-full object-cover object-center"
            >
              <path
                d="M1 1L15 15M29 29L15 15M15 15L29 1M15 15L1 29"
                stroke="black"
                stroke-width="1"
              />
            </svg>
          </button>

          <!--Modal body-->
          <div v-if="$slots.body" :class="['relative', props.bodyClasses]">
            <slot name="body" />
          </div>

          <!--Modal footer-->
          <div v-if="$slots.footer" class="relative px-6">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
  .modal-title {
    @apply text-c2sm leading-[20px] lg:leading-normal lg:text-cbase font-bold uppercase;
  }

  /* Transitions */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.1s linear;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
